/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { catchError, of, switchMap, take, tap, EMPTY } from 'rxjs';

import { ArchetypeReportService, ArchetypeReportPriceResponse } from '../../../resources/archetype-report.service';
import { ArchetypeReportDiscountService } from '../../../resources/archetype-report-discount.service';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { SuccessService } from '../../../../../services/success.service';
import { NexleaderStripeElementModalComponent } from '../../../../store/components/stripe-element-modal/stripe-element-modal.component';
import { NexleaderCurrencyComponent } from '../../../../store/components/currency/currency.component';

@Component({
  selector: 'app-purchase-archetype-report-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NexleaderCurrencyComponent],
  templateUrl: './purchase-archetype-report-modal.component.html',
})
export class PurchaseArchetypeReportModalComponent implements OnInit {
  @Input() surveyResultId!: string;
  @Input() userId!: string;
  @Input() groupId?: string;
  @Input() surveyType: 'faith_based' | 'values_based' = 'values_based';
  @Input() purchasedByUserId?: string; // For admin/coach purchases
  
  @Output() onPurchase = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();

  price: ArchetypeReportPriceResponse | null = null;
  discountCode: string = '';
  appliedDiscount: string | null = null;
  discountError: string | null = null;
  isLoadingPrice: boolean = false;
  isProcessing: boolean = false;
  /**
   * Set true once payment is complete and the /purchase request is in flight.
   * The backend runs the deterministic engine + content load + AI personalization
   * (6 Anthropic prompts) before responding, which takes ~15–30s. We swap the
   * modal body for a friendly generating-state UI for the duration so users
   * don't think it stalled.
   */
  isGeneratingReport: boolean = false;

  constructor(
    private archetypeReportService: ArchetypeReportService,
    private archetypeReportDiscountService: ArchetypeReportDiscountService,
    private errorHandler: ErrorHandlerService,
    private successService: SuccessService,
    private modalService: BsModalService,
    public bsModalRef: BsModalRef
  ) {}

  ngOnInit(): void {
    this.loadPrice();
  }

  loadPrice(): void {
    this.isLoadingPrice = true;
    this.archetypeReportService
      .calculatePrice({
        surveyResultId: this.surveyResultId,
        archetypeReportDiscountName: this.appliedDiscount || undefined,
        groupId: this.groupId,
      })
      .pipe(
        tap((response) => {
          this.price = response;
          this.isLoadingPrice = false;
        }),
        catchError((error) => {
          this.errorHandler.handleError(error);
          this.isLoadingPrice = false;
          return of(null);
        })
      )
      .subscribe();
  }

  applyDiscount(): void {
    if (!this.discountCode.trim()) {
      return;
    }

    this.discountError = null;
    this.archetypeReportDiscountService
      .getByName(this.discountCode)
      .pipe(
        tap((response) => {
          if (response && response.refreshOnly) {
            // refreshOnly codes are valid only on the /regenerate flow. The
            // backend already refuses to apply them here, but reject up front
            // so the price doesn't look like it changed. (Steve 2026-07-24)
            this.discountError =
              'This code can only be used to refresh an existing report, not for a new purchase.';
          } else if (response) {
            this.appliedDiscount = this.discountCode;
            this.discountCode = '';
            this.loadPrice();
          } else {
            this.discountError = 'Invalid discount code';
          }
        }),
        catchError((error) => {
          this.discountError = 'Error validating discount code';
          return of(null);
        })
      )
      .subscribe();
  }

  removeDiscount(): void {
    this.appliedDiscount = null;
    this.loadPrice();
  }

  purchase(): void {
    if (!this.price || this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    // If price is 0, no payment needed
    if (this.price.currencyQuantity <= 0) {
      this.completePurchase(null).subscribe();
      return;
    }

    // Open Stripe modal for payment
    const stripeModalRef = this.modalService.show(
      NexleaderStripeElementModalComponent,
      {
        initialState: {
          title: 'IPSAT 2.0 Archetype Report',
          description: 'Unlock your personalized archetype analysis',
          currencyCode: this.price.currencyCode?.toLowerCase() || 'usd',
          amount: Math.trunc(this.price.currencyQuantity * 100),
        },
      }
    );

    stripeModalRef.onHide?.pipe(take(1)).subscribe(() => {
      this.isProcessing = false;
    });

    stripeModalRef.content?.tokenGenerated
      .pipe(take(1))
      .pipe(
        tap(() => stripeModalRef.hide()),
        switchMap((token) => {
          if (!token) {
            this.isProcessing = false;
            return EMPTY;
          }
          return this.completePurchase(token);
        })
      )
      .subscribe();
  }

  private completePurchase(stripeToken: any) {
    this.isGeneratingReport = true;
    return this.archetypeReportService
      .purchase({
        surveyResultId: this.surveyResultId,
        stripeToken: stripeToken,
        price: this.price!,
        archetypeReportDiscountName: this.appliedDiscount || undefined,
        purchasedByUserId: this.purchasedByUserId,
      })
      .pipe(
        tap((archetypeReport) => {
          this.isProcessing = false;
          this.isGeneratingReport = false;
          this.successService.handle({ message: 'Your IPSAT 2.0 Report is ready!' });
          this.onPurchase.emit(archetypeReport);
          this.bsModalRef.hide();
        }),
        catchError((error) => {
          this.isProcessing = false;
          this.isGeneratingReport = false;
          this.errorHandler.handleError(error);
          return of(null);
        })
      );
  }

  cancel(): void {
    this.onCancel.emit();
    this.bsModalRef.hide();
  }
}
